import { debounce } from "lodash";
import { useEffect, useState } from "react";

import { useChatStore } from "~/stores/chatStore";
import { useAuth } from "~/components/general";
import { isSupportTicketChannel } from "~/constants/supportTickets";
import { WORK_ORDER_BROADCAST_TOPICS } from "~/constants/workOrders";
import usePubnubInstance from "~/hooks/_pubnub-unread-messages";
import { broadcaster } from "~/utils/_appBroadcast";

const DEBOUNCE_FULL_REFRESH_WAIT = 5000; // 5 seconds

// Any customer-portal page. Portal users have no read grant on internal-notes
// channels, so reading them 403s — this covers the whole workspace tree rather
// than just `/requests/`, since Support Tickets brought internal-notes channel
// names into portal-facing payloads too. A dual-role user browsing the portal
// loses nothing permanently: the unfiltered full refresh still counts them.
const isPortalPath = () =>
  typeof window !== "undefined" &&
  window.location.pathname.includes("/app/workspace/");

const filterUnreadRefreshChannels = (channelIds) => {
  if (!isPortalPath()) return channelIds;
  return channelIds.filter((id) => !id.includes("internal-notes"));
};

const useNotification = () => {
  const { user, chat: chatInstance } = useAuth();

  const unreadChannelMessages = useChatStore((s) => s.unreadChannelMessages);
  const allMembershipChannelsCoil = useChatStore(
    (s) => s.allMembershipChannels,
  );

  const [unreadLabels, setUnreadLabels] = useState({
    openTickets: 0,
    myTickets: 0,
    requests: 0,
    supportTickets: 0,
  });
  const userOemId = user?.oem?._id;

  const { getAllUnreadMessageCount, refreshChannelsUnread } =
    usePubnubInstance(chatInstance)();

  useEffect(() => {
    const allUnreadChannels = Object.keys(
      unreadChannelMessages?.channels || {},
    );
    const hasAllMembershipChannels =
      Object.keys(allMembershipChannelsCoil).length > 0;
    const isFollowersMyWorkOrdersAllowed =
      user?.oem?.allowFollowersMyWorkOrders;

    if (allUnreadChannels.length && hasAllMembershipChannels) {
      let myWorkOrdersUnreadCount = 0;
      let requestsUnreadCount = 0;
      let supportTicketsUnreadCount = 0;
      const totalUnread = allUnreadChannels.reduce((accumulator, channel) => {
        const channelInfo = allMembershipChannelsCoil[channel]?.custom;
        // Channels outside the user's own OEM belong to a connection they are a
        // portal contact on — they are counted by the portal sidebar, per
        // connection, and must not reach the OEM navigation. This split comes
        // first for support tickets too: bucketing them by suffix before the
        // oemId check put another OEM's support chats in this OEM's Tickets
        // badge, which is where dual-role users saw an inflated count.
        if (!userOemId || !channel.includes(userOemId)) {
          requestsUnreadCount += unreadChannelMessages.channels[channel];
          return accumulator;
        }
        // Support Ticket channels have their own badge budget — they must never
        // leak into the Work Order buckets (and vice versa).
        if (isSupportTicketChannel(channel)) {
          supportTicketsUnreadCount += unreadChannelMessages.channels[channel];
          return accumulator;
        }
        const channelAssignees = JSON.parse(channelInfo?.assignees || "[]");
        const channelFollowers = JSON.parse(channelInfo?.followers || "[]");
        const isMyWorkOrder =
          channelAssignees.includes(user?._id) ||
          (isFollowersMyWorkOrdersAllowed &&
            channelFollowers.includes(user?._id));

        if (isMyWorkOrder) {
          myWorkOrdersUnreadCount += unreadChannelMessages.channels[channel];
        }

        return accumulator + unreadChannelMessages.channels[channel];
      }, 0);
      setUnreadLabels((prev) => ({
        ...prev,
        openTickets: totalUnread,
        myTickets: myWorkOrdersUnreadCount,
        requests: requestsUnreadCount,
        supportTickets: supportTicketsUnreadCount,
      }));
    }
  }, [
    unreadChannelMessages?.channels,
    allMembershipChannelsCoil,
    user?.oem?.allowFollowersMyWorkOrders,
    userOemId,
  ]);

  useEffect(() => {
    if (!chatInstance) return;
    // Every entry point is fire-and-forget, so a rejection here would only
    // ever have shown up as an uncaught PubNubError — with every badge in the
    // app silently reading zero. The hook resolves rather than rejects now;
    // this is the backstop.
    const runScan = (fn, ...args) =>
      Promise.resolve()
        .then(() => fn(...args))
        .catch((error) => console.error("[unread] refresh failed", error));

    // Full scan once on load to build the unread badge state.
    runScan(getAllUnreadMessageCount);

    // The full re-pagination is expensive, so debounce it to coalesce bursts
    // (the targeted refresh below is cheap and runs immediately).
    const debouncedFullRefresh = debounce(
      () => runScan(getAllUnreadMessageCount),
      DEBOUNCE_FULL_REFRESH_WAIT,
    );

    // On a membership change, refresh ONLY the affected channels instead of
    // re-paginating every membership. Fall back to a full refresh only when
    // the payload lacks channels.
    const handleMembershipUpdated = (channels) => {
      const ids = filterUnreadRefreshChannels(
        [channels].flat().filter(Boolean),
      );
      if (ids.length) runScan(refreshChannelsUnread, ids);
      else debouncedFullRefresh();
    };
    const unsubscribeAssigneeFollowersUpdate = broadcaster.subscribe(
      WORK_ORDER_BROADCAST_TOPICS.MEMBERSHIP_UPDATED,
      handleMembershipUpdated,
    );

    // A chat message arrived out-of-band (see the chat-message branch in
    // useGlobalListener). Same response as a membership change — recount the
    // named channels — but never the debounced full refresh: a busy chat would
    // trigger a full membership re-pagination per burst, and an event with no
    // channel tells us nothing to recount.
    const handleUnreadRefresh = (channels) => {
      const ids = filterUnreadRefreshChannels(
        [channels].flat().filter(Boolean),
      );
      if (ids.length) runScan(refreshChannelsUnread, ids);
    };
    const unsubscribeUnreadRefresh = broadcaster.subscribe(
      WORK_ORDER_BROADCAST_TOPICS.UNREAD_REFRESH,
      handleUnreadRefresh,
    );

    return () => {
      debouncedFullRefresh.cancel();
      unsubscribeAssigneeFollowersUpdate(
        WORK_ORDER_BROADCAST_TOPICS.MEMBERSHIP_UPDATED,
        handleMembershipUpdated,
      );
      unsubscribeUnreadRefresh(
        WORK_ORDER_BROADCAST_TOPICS.UNREAD_REFRESH,
        handleUnreadRefresh,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatInstance]);

  return { unreadLabels };
};

export default useNotification;
